import React, { useRef, useEffect, useState } from 'react'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import styled from 'styled-components'
import { SectionContent } from '../common-styled-component/section'
import DcOverlaySlider from '../dc-overlay-slider/dc-overlay-slider'

interface ScannerProps {
  isOpen: boolean
  onClose: () => void
  scannedSKU?: string
  setScannedSKU?: (sku: string) => void
  handleScan?: (sku: string) => void
  scannerRef?: React.RefObject<HTMLVideoElement>
}

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: #f9fafb;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ScannerOverlayFrame = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 200px;
  height: 200px;
  border: 2px solid #3b82f6;
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  pointer-events: none;
`

const StatusText = styled.div`
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
`

const ScannedResult = styled.div`
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 6px;
  padding: 12px;
  margin-top: 16px;
`

const ScannedText = styled.div`
  font-size: 14px;
  color: #0c4a6e;
  font-weight: 500;
`

const DcScanner: React.FC<ScannerProps> = ({
  isOpen,
  onClose,
  scannedSKU,
  setScannedSKU,
  handleScan,
  scannerRef,
}) => {
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [isScanning, setIsScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDevice, setSelectedDevice] = useState<string>('')
  const videoRef = useRef<HTMLVideoElement>(null)
  const codeReader = useRef<BrowserMultiFormatReader | null>(null)
  const trackingInputRef = React.useRef<HTMLInputElement>(null)
  // Use provided scannerRef or create our own
  const videoElementRef = scannerRef || videoRef

  const startScanner = async () => {
    try {
      setError(null)
      setIsScanning(true)

      // Get available video devices
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput',
      )
      setDevices(videoDevices)

      if (videoDevices.length === 0) {
        throw new Error('No camera devices found')
      }

      // Start barcode detection with ZXing
      startBarcodeDetection()
    } catch (err) {
      console.error('Error starting scanner:', err)
      setError(err instanceof Error ? err.message : 'Failed to start camera')
      setIsScanning(false)
    }
  }

  const stopScanner = () => {
    // Stop barcode reader
    if (codeReader.current) {
      try {
        codeReader.current.reset()
        codeReader.current = null
      } catch (err) {
        console.error('Error stopping barcode reader:', err)
      }
    }

    // Stop video stream
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
    setIsScanning(false)
    setError(null)
  }

  const startBarcodeDetection = () => {
    if (!videoElementRef.current) return

    try {
      // Initialize the barcode reader - BrowserMultiFormatReader supports all formats by default
      codeReader.current = new BrowserMultiFormatReader()

      // Get the device ID to use
      const deviceId = selectedDevice || devices[0]?.deviceId || null

      // Start decoding from video stream
      codeReader.current.decodeFromVideoDevice(
        deviceId,
        videoElementRef.current,
        (result: Result | null, error: any) => {
          if (result) {
            const scannedCode = result.getText()
            const format = result.getBarcodeFormat()

            if (setScannedSKU) {
              setScannedSKU(scannedCode)
            }

            if (handleScan) {
              handleScan(scannedCode)
            }

            // Stop scanning after successful scan
            stopScanner()
            onClose()
          }

          if (error && error.name !== 'NotFoundException') {
            console.error('Scanning error:', error)
            setError('Error scanning barcode: ' + error.message)
          }
        },
      )

      // Store the stream for cleanup
      if (codeReader.current) {
        // @ts-ignore - accessing internal stream
        setStream(codeReader.current.stream)
      }
    } catch (err) {
      console.error('Error starting barcode detection:', err)
      setError('Failed to start barcode detection')
      setIsScanning(false)
    }
  }

  useEffect(() => {
    if (isOpen) {
      startScanner()
    } else {
      stopScanner()
    }

    return () => {
      stopScanner()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && selectedDevice) {
      stopScanner()
      setTimeout(startScanner, 100)
    }
  }, [selectedDevice])

  // Focus input when scanner opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        if (trackingInputRef.current) {
          trackingInputRef.current.focus()
        }
      }, 100)
    }
  }, [isOpen])

  return (
    <DcOverlaySlider
      title="Scan Barcode/QR Code"
      isOpen={isOpen}
      onClose={onClose}
      width="40%"
    >
      <SectionContent>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <VideoContainer>
            <Video
              ref={videoElementRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%' }}
            />
            <ScannerOverlayFrame />
          </VideoContainer>

          {error && (
            <StatusText style={{ color: '#ef4444' }}>Error: {error}</StatusText>
          )}

          {!error && isScanning && (
            <StatusText>
              <div>Point camera at barcode or QR code...</div>
              <div
                style={{ fontSize: '12px', marginTop: '4px', color: '#9ca3af' }}
              >
                💡 Tip: For barcodes, ensure good lighting and hold steady. Try
                different distances.
              </div>
            </StatusText>
          )}

          {scannedSKU && (
            <ScannedResult>
              <ScannedText>Scanned: {scannedSKU}</ScannedText>
            </ScannedResult>
          )}

          {/* Manual Input for Testing */}
          <div style={{ marginTop: '16px' }}>
            <div
              style={{
                fontSize: '14px',
                marginBottom: '8px',
                color: '#6b7280',
              }}
            >
              Or manually enter tracking ID for testing:
            </div>
            <input
              type="text"
              placeholder="Enter tracking ID manually"
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
              }}
              ref={trackingInputRef}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  const value = e.currentTarget.value.trim()
                  if (setScannedSKU) {
                    setScannedSKU(value)
                  }
                  if (handleScan) {
                    handleScan(value)
                  }
                  e.currentTarget.value = ''
                }
              }}
              autoFocus={true}
            />
          </div>
        </div>
      </SectionContent>
    </DcOverlaySlider>
  )
}

export default DcScanner
